import { useEffect, useState } from "react";
import { Trophy, Flame, Sparkles, Moon, Star, Medal, Share2, ArrowRight, X, Copy, MessageSquareHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICON_MAP = { moon: Moon, flame: Flame, sparkles: Sparkles, trophy: Trophy, star: Star, medal: Medal };
const TIER_RING = {
  bronze:   "from-amber-700 via-amber-500 to-amber-300",
  silver:   "from-slate-400 via-slate-200 to-slate-50",
  gold:     "from-yellow-600 via-amber-400 to-yellow-200",
  platinum: "from-indigo-500 via-cyan-300 to-emerald-300",
};
const TIER_ACCENT = {
  bronze: "text-amber-300",
  silver: "text-slate-100",
  gold: "text-yellow-200",
  platinum: "text-indigo-200",
};

/** Simple confetti — random colored squares falling. Pure CSS keyframes. */
function Confetti({ count = 60 }) {
  const pieces = Array.from({ length: count }).map((_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 1.5;
    const duration = 2.6 + Math.random() * 2;
    const size = 6 + Math.random() * 8;
    const rot = Math.random() * 360;
    const colors = ["#818CF8", "#10B981", "#F59E0B", "#F43F5E", "#38BDF8", "#FDE68A"];
    const color = colors[i % colors.length];
    return (
      <span
        key={i}
        style={{
          position: "absolute",
          top: "-6%",
          left: `${left}%`,
          width: size,
          height: size,
          background: color,
          transform: `rotate(${rot}deg)`,
          animation: `circa-fall ${duration}s ${delay}s linear forwards`,
          borderRadius: i % 3 === 0 ? "999px" : "2px",
          opacity: 0.9,
        }}
      />
    );
  });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces}
      <style>{`
        @keyframes circa-fall {
          0%   { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
        }
        @keyframes circa-badge-pop {
          0%   { transform: scale(0.4) rotate(-14deg); opacity: 0; filter: blur(6px); }
          55%  { transform: scale(1.12) rotate(4deg); opacity: 1; filter: blur(0); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes circa-badge-glow {
          0%,100% { box-shadow: 0 0 60px 12px rgba(129,140,248,0.4); }
          50%     { box-shadow: 0 0 90px 24px rgba(129,140,248,0.65); }
        }
        @keyframes circa-text-rise {
          0%   { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/**
 * Full-screen badge unlock overlay.
 *
 * Props:
 *  - badges: array of newly-earned badge objects (may be empty → renders nothing)
 *  - onClose: called after user dismisses the entire sequence
 *  - onGoToBadges: optional, called when user taps "View all badges"
 *  - streak: optional streak summary { current, longest }
 *  - userName / persona: for share preview
 */
export default function BadgeUnlockOverlay({ badges, onClose, onGoToBadges, onInviteReview, streak, userName, persona }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (badges && badges.length > 0) {
      setIdx(0);
      // slight delay so we get an "entrance" feel
      const t = setTimeout(() => setVisible(true), 40);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [badges]);

  if (!badges || badges.length === 0 || !visible) return null;

  const b = badges[idx];
  const Icon = ICON_MAP[b.icon] || Trophy;
  const ring = TIER_RING[b.tier] || TIER_RING.silver;
  const accent = TIER_ACCENT[b.tier] || "text-slate-100";
  const isLast = idx === badges.length - 1;

  const shareText =
    `🏅 I just unlocked "${b.name}" on CircaShift.\n` +
    `${b.description}\n` +
    `Current streak: ${streak?.current ?? 0} nights · Longest: ${streak?.longest ?? 0}.\n` +
    `Building sleep that bends around my shifts — one recovery night at a time.`;

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `CircaShift · ${b.name}`, text: shareText }); }
      catch { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success("Copied to clipboard", { description: "Paste it into your team channel." });
      } catch {
        toast.error("Couldn't copy — long-press to select the text.");
      }
    }
  };

  const advance = () => {
    if (isLast) {
      setVisible(false);
      // Milestone badges (silver+) → prompt the user to leave a review
      const milestone = ["streak_7", "streak_14", "streak_30", "good_10", "good_50", "longest_7"];
      const earnedMilestone = badges.find((x) => milestone.includes(x.id));
      setTimeout(() => {
        onClose?.();
        if (earnedMilestone && onInviteReview) {
          toast(
            `You just hit "${earnedMilestone.name}" — share your story?`,
            {
              icon: <MessageSquareHeart className="w-4 h-4 text-indigo-300" />,
              description: "A short review helps other shift workers discover CircaShift.",
              duration: 12000,
              action: {
                label: "Leave a review",
                onClick: () => onInviteReview(),
              },
            }
          );
        }
      }, 200);
    } else {
      setIdx((i) => i + 1);
    }
  };

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 200);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
      data-testid="badge-unlock-overlay"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-[#04060B]/90 backdrop-blur-xl"
        onClick={dismiss}
      />
      {/* radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vmin] h-[80vmin] rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50vmin] h-[50vmin] rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <Confetti />

      {/* card */}
      <div
        key={b.id}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-gradient-to-b from-[#0B1220] to-[#07090E] p-8 text-center shadow-2xl"
        style={{ animation: "circa-badge-pop 700ms cubic-bezier(0.2, 0.9, 0.3, 1.4) both" }}
      >
        <button
          onClick={dismiss}
          data-testid="badge-unlock-close"
          className="absolute top-3 right-3 text-slate-500 hover:text-slate-200 transition"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="pill mx-auto mb-6 bg-indigo-950/60 border-indigo-500/40 text-indigo-200">
          <Sparkles className="w-3.5 h-3.5" /> Badge Unlocked
        </div>

        <div className="relative mx-auto flex items-center justify-center">
          <div
            className={`w-32 h-32 rounded-3xl bg-gradient-to-br ${ring} flex items-center justify-center`}
            style={{ animation: "circa-badge-glow 2.4s ease-in-out infinite" }}
          >
            <Icon className="w-16 h-16 text-white drop-shadow-lg" />
          </div>
          <div className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full bg-[#0B1220] border border-white/10 flex items-center justify-center shadow-lg">
            <Trophy className={`w-5 h-5 ${accent}`} />
          </div>
        </div>

        <div style={{ animation: "circa-text-rise 480ms 180ms both" }}>
          <h2 className={`font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-6 ${accent}`} data-testid="badge-unlock-name">
            {b.name}
          </h2>
          <div className="mt-1 uppercase tracking-widest text-[10px] font-mono-ui text-slate-400">
            {b.tier} · {b.kind.replace("_", " ")}
          </div>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mt-3 max-w-sm mx-auto">
            {b.description}
          </p>
        </div>

        {streak && (
          <div className="grid grid-cols-3 gap-2 mt-6" style={{ animation: "circa-text-rise 480ms 320ms both" }}>
            <MiniStat label="Streak" value={`${streak.current || 0}d`} tint="text-emerald-300" />
            <MiniStat label="Longest" value={`${streak.longest || 0}d`} tint="text-amber-300" />
            <MiniStat label="Tier" value={b.tier} tint={accent} />
          </div>
        )}

        <div className="mt-7 flex flex-col sm:flex-row gap-2 justify-center" style={{ animation: "circa-text-rise 480ms 460ms both" }}>
          <Button
            onClick={share}
            data-testid="badge-unlock-share"
            variant="outline"
            className="rounded-full bg-transparent border-white/15 text-slate-100 hover:bg-white/5"
          >
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
          <Button
            onClick={advance}
            data-testid="badge-unlock-continue"
            className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            {isLast ? "Continue" : `Next badge (${idx + 2}/${badges.length})`} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {onGoToBadges && (
          <button
            onClick={() => { setVisible(false); setTimeout(() => { onClose?.(); onGoToBadges(); }, 180); }}
            data-testid="badge-unlock-goto"
            className="mt-4 text-xs uppercase tracking-widest font-mono-ui text-slate-500 hover:text-slate-300 transition"
          >
            View all badges →
          </button>
        )}

        {badges.length > 1 && (
          <div className="mt-4 flex items-center justify-center gap-1.5">
            {badges.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-indigo-300" : "w-1.5 bg-slate-700"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint = "text-indigo-300" }) {
  return (
    <div className="bg-slate-900/70 border border-white/10 rounded-xl p-2.5">
      <div className="text-[9px] uppercase tracking-widest font-mono-ui text-slate-500">{label}</div>
      <div className={`font-display text-lg font-bold ${tint}`}>{value}</div>
    </div>
  );
}
